import React, { useCallback, useEffect, useState } from 'react'
import { useAdvancedOverviewState } from '../api/AdvancedOverviewProvider'
import { usePersistedState } from '../api/localStore'
import { useTabNavigation } from '../dashboard/tabNavigationContext'
import { useEditMode } from '../dashboard/useEditMode'
import { useProductUpdates, useReportFirstRunTourRunning } from '../productUpdates/ProductUpdatesProvider'
import { TOUR_STEPS, TourStepDefinition, selectTourSteps } from './steps'
import { useTourRequest } from './tourRequest'
import TourRunner from './TourRunner'

export const TOUR_SEEN_KEY = 'advanced.tour.seen.v1'

type Props = { setCareTeamChatOpen?: (open: boolean) => void }

const AdvancedTour = ({ setCareTeamChatOpen }: Props) => {
  const tourRequest = useTourRequest()
  const overview = useAdvancedOverviewState()
  const [editMode] = useEditMode()
  const productUpdatesOpen = useProductUpdates().view !== null
  const { openTab, hasTab } = useTabNavigation()
  const [seen, setSeen] = usePersistedState(TOUR_SEEN_KEY, false)
  const [steps, setSteps] = useState<TourStepDefinition[] | null>(null)
  const reportFirstRunTourRunning = useReportFirstRunTourRunning()
  const endTour = useCallback(() => {
    reportFirstRunTourRunning(false)
    setSteps(null)
  }, [reportFirstRunTourRunning])

  const ready = overview.status === 'ready'
  const wanted = tourRequest !== null && (tourRequest.requested || (!seen && !editMode && !productUpdatesOpen))

  useEffect(() => {
    if (!tourRequest || !ready || !wanted) {
      return
    }
    tourRequest.onHandled()
    if (steps) {
      return
    }
    setSeen(true)
    // Reported here rather than after the re-render, so an auto-open decided in this same commit waits.
    reportFirstRunTourRunning(true)
    setSteps(selectTourSteps(TOUR_STEPS, hasTab))
  }, [tourRequest, ready, wanted, steps, hasTab, setSeen, reportFirstRunTourRunning])

  useEffect(() => () => reportFirstRunTourRunning(false), [reportFirstRunTourRunning])

  if (!steps) {
    return null
  }

  return <TourRunner steps={steps} openTab={openTab} onEnd={endTour} setCareTeamChatOpen={setCareTeamChatOpen} />
}

export default AdvancedTour
